import React from 'react';
import { format } from 'date-fns';
import { Check, X, Calendar, User, MapPin, Clock, Trash2 } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { useCustodyParents } from '../hooks/useCustodyParents';
import toast from 'react-hot-toast';
import {
  usePendingOverrides,
  useApproveOverride,
  useRejectOverride,
  PendingOverride,
} from '../hooks/useAvailability';

// at_parent1 / at_parent2 (and their legacy codes at_toms / at_julies) are
// labelled with the family's own custody parent names at render time.
const overrideTypeLabels: Record<string, string> = {
  work: "Work",
  school: "School",
  friend: "Friend's House",
  event: "Event/Activity",
  other: "Other"
};

export function AvailabilityApprovals() {
  const { user } = useAuth();
  const custodyParents = useCustodyParents();
  const overrideLabel = (type: string) => {
    if (type === 'at_parent1' || type === 'at_toms') return `At ${custodyParents.primary}'s House`;
    if (type === 'at_parent2' || type === 'at_julies') return `At ${custodyParents.other}'s House`;
    return overrideTypeLabels[type] || type;
  };

  // React Query hooks
  const { data: pendingOverrides = [], isLoading: loading } = usePendingOverrides(!!user?.is_parent);
  const approveMutation = useApproveOverride();
  const rejectMutation = useRejectOverride();

  const handleApprove = async (overrideId: string) => {
    try {
      await approveMutation.mutateAsync(overrideId);
      toast.success('Override approved!');
    } catch (error) {
      console.error('Error approving override:', error);
      toast.error('Failed to approve override');
    }
  };

  const handleReject = async (overrideId: string) => {
    try {
      await rejectMutation.mutateAsync(overrideId);
      toast.success('Override rejected');
    } catch (error) {
      console.error('Error rejecting override:', error);
      toast.error('Failed to reject override');
    }
  };

  const handleDismiss = async (overrideId: string) => {
    try {
      await rejectMutation.mutateAsync(overrideId);
      toast.success('Past request dismissed');
    } catch (error) {
      console.error('Error dismissing override:', error);
      toast.error('Failed to dismiss request');
    }
  };

  if (!user?.is_parent) {
    return null;
  }

  if (loading) {
    return <div className="text-center py-4">Loading...</div>;
  }

  if (!pendingOverrides || pendingOverrides.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-xl font-bold mb-4">Availability Approvals</h2>
        <p className="text-gray-500">No pending availability requests</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <h2 className="text-xl font-bold mb-4">
        Availability Approvals ({pendingOverrides.length})
      </h2>
      
      <div className="space-y-4">
        {pendingOverrides.map(override => (
          <div key={override.id} className="border rounded-lg p-4 bg-gray-50">
            <div className="flex justify-between items-start mb-3">
              <div className="flex items-center gap-2">
                <User className="w-5 h-5 text-gray-500" />
                <span className="font-semibold">{override.user_name}</span>
              </div>
              <div className="flex gap-2">
                {override.days_until < 0 ? (
                  <button
                    onClick={() => handleDismiss(override.id)}
                    className="bg-gray-500 text-white px-3 py-1 rounded hover:bg-gray-600 flex items-center gap-1"
                    title="Dismiss past request"
                  >
                    <Trash2 className="w-4 h-4" />
                    Dismiss
                  </button>
                ) : (
                  <>
                    <button
                      onClick={() => handleApprove(override.id)}
                      className="bg-green-500 text-white px-3 py-1 rounded hover:bg-green-600 flex items-center gap-1"
                      title="Approve"
                    >
                      <Check className="w-4 h-4" />
                      Approve
                    </button>
                    <button
                      onClick={() => handleReject(override.id)}
                      className="bg-red-500 text-white px-3 py-1 rounded hover:bg-red-600 flex items-center gap-1"
                      title="Reject"
                    >
                      <X className="w-4 h-4" />
                      Reject
                    </button>
                  </>
                )}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-2 text-sm">
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-gray-400" />
                <span>{format(new Date(override.override_date + 'T12:00:00'), 'MMM d, yyyy')}</span>
              </div>
              
              <div className="flex items-center gap-2">
                <MapPin className="w-4 h-4 text-gray-400" />
                <span>{overrideLabel(override.override_type)}</span>
              </div>

              {!override.all_day && (
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-gray-400" />
                  <span>{override.start_time} - {override.end_time}</span>
                </div>
              )}

              {override.reason && (
                <div className="col-span-2">
                  <span className="text-gray-600">Reason: {override.reason}</span>
                </div>
              )}
            </div>

            <div className="mt-2 text-sm">
              {override.days_until >= 3 ? (
                <span className="text-green-600">
                  {override.days_until} days notice - Chores will be auto-redistributed
                </span>
              ) : override.days_until >= 0 ? (
                <span className="text-orange-600">
                  {override.days_until} days notice - Child must offer chores for bidding
                </span>
              ) : (
                <span className="text-red-600">
                  Past date - Cannot be approved
                </span>
              )}
            </div>

            <div className="mt-2 text-xs text-gray-500">
              Requested {format(new Date(override.created_at), 'MMM d h:mm a')}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}